/* hs-eslint ignored failing-rules */
/* eslint-disable no-prototype-builtins */

'use es6';

import Raven from 'raven-js';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import findSchemaDefinition from '../../../lib/api-explorer/find-schema-definition';
import capitalizeFirstLetter from '../../../lib/utils/capitalizeFirstLetter';
import TypesList from '../components/TypesList';
import Markdown from '../../Doc/components/Markdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OAS_COMPOSABLE_TYPES = ['oneOf', 'anyOf', 'allOf'];
export const flatten = list => list.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []);
const getName = (parent, prop) => {
  if (!parent) return prop;
  if (parent[parent.length - 1] === ' ') return `${parent}${prop}`;
  return `${parent}.${prop}`;
};
const refToConstantCase = $ref => {
  // i.e. ActionHookType -> ACTION_HOOK_TYPE
  return $ref.split('/').slice(-1)[0].replace(/[A-Z]/g, (match, index) => {
    return index === 0 ? match : `_${match}`;
  }).toUpperCase();
};
export const getOneOfTypes = (itemsSet, oas) => {
  const types = [];
  for (const items of itemsSet) {
    if (items.$ref) {
      const def = findSchemaDefinition(items.$ref, oas);
      types.push(`${def.title || refToConstantCase(items.$ref)}`);
    } else if (items.$$ref) {
      const def = findSchemaDefinition(items.$$ref, oas);
      types.push(`${def.title}`);
    }
  }
  return types;
};
const getTypeDescriptionComponent = (isOneOf, value, itemsSet, oas) => {
  if (isOneOf) {
    const endpointDesc = value.description ? `${value.description} ` : '';
    const types = isOneOf ? getOneOfTypes(itemsSet, oas) : undefined;
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(Markdown, {
        children: endpointDesc
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "developer-docs-shared-components.oneOfDescription_jsx",
        elements: {
          TypesList
        },
        options: {
          types
        }
      })]
    });
  } else {
    return value.description;
  }
};
function getAllOfParams(items, parent, level, oas) {
  const params = [];
  const flattenObj = def => flattenObject(def, parent, level, oas);
  for (const item of items) {
    if (item.$ref) {
      const def = findSchemaDefinition(item.$ref, oas);
      if (Object.keys(def).find(key => OAS_COMPOSABLE_TYPES.includes(key))) {
        // allOf components cannot resolve other composable types
        continue;
      } else {
        params.push(flattenObj(def));
      }
    } else if (item.type && item.type === 'object') {
      params.push(flattenObj(item));
    }
  }
  return params;
}
function flattenOneOfSchema(obj, oas, level) {
  let arr = [];
  for (const oneOfType of obj.oneOf) {
    const def = findSchemaDefinition(oneOfType.$ref, oas);
    const parent = refToConstantCase(oneOfType.$ref);
    arr = arr.concat(flattenResponseSchema(def, oas, parent, level));
  }
  return arr;
}
function flattenAddtionalProperties(obj, oas) {
  let arr = [];
  const def = findSchemaDefinition(obj.additionalProperties.$ref, oas);
  const parent = refToConstantCase(obj.additionalProperties.$ref);
  arr = arr.concat(flattenResponseSchema(def, oas, parent, 1));
  return arr;
}
function flattenArray(itemsSet, oas, parent, prop, value, level) {
  let array = [];
  let isOneOf = false;
  const newParent = parent ? `${parent}.` : '';

  // Array root level types
  if (itemsSet.$ref) {
    itemsSet = findSchemaDefinition(itemsSet.$ref, oas);
  }
  if (itemsSet.oneOf) {
    itemsSet = itemsSet.oneOf;
    isOneOf = true;
  }
  if (itemsSet.type === 'object') {
    if (itemsSet.hasOwnProperty('properties')) {
      return flattenObject(itemsSet, `${newParent}${prop}[]`, level, oas);
    } else if (itemsSet.hasOwnProperty('additionalProperties')) {
      return [{
        name: `${newParent}${prop}[]`,
        type: `[${capitalizeFirstLetter(itemsSet.type || 'object')}]`,
        description: value.description
      }];
    }
    Raven.captureMessage('Unexpected itemsSet in flattenArray', {
      extra: {
        itemsSet
      }
    });
    return array;
  }

  // itemsSet contains direct properties that need to be flattened
  for (let i = 0; i < itemsSet.length; i++) {
    let items = itemsSet[i];
    if (items.$ref) {
      items = findSchemaDefinition(items.$ref, oas);
    }

    // When "oneOf" is the type only display it once
    if ((items.type || items.allOf) && (!isOneOf || isOneOf && i === 0)) {
      array.push({
        name: getName(parent, prop),
        type: `[${capitalizeFirstLetter(items.type || 'object')}]`,
        description: getTypeDescriptionComponent(isOneOf, value, itemsSet, oas)
      });
    }
    if (items.allOf) {
      array = array.concat(getAllOfParams(items.allOf, `${newParent}${prop}.${refToConstantCase(itemsSet[i].$ref)}`, level, oas));
    }
    if (items.type === 'object') {
      array.push(flattenResponseSchema(items, oas, isOneOf ? `${newParent}${prop}[].${items.title}` : `${newParent}${prop}[]`, level + 1));
    }
  }
  return array;
}
function flattenObject(obj, parent, level, oas) {
  return flatten(Object.keys(obj.properties).map(prop => {
    let value = obj.properties[prop];
    const array = [];
    if (value.$ref) {
      value = findSchemaDefinition(value.$ref, oas);
    }
    if (value.type === 'object') {
      array.push(flattenResponseSchema(value, oas, getName(parent, prop), level + 1));
    }
    if (value.type === 'array' && value.items) {
      return array.concat(flattenArray(value.items, oas, parent, prop, value, level + 1));
    }
    if (value.oneOf) {
      if (level + 1 > 2) {
        array.push({
          description: getTypeDescriptionComponent(true, '', value.oneOf, oas),
          type: capitalizeFirstLetter(value.type || 'object'),
          name: getName(parent, prop)
        });
        return array;
      } else {
        return array.concat(flattenOneOfSchema(value, oas, level + 1, getName(parent, prop)));
      }
    }
    array.unshift({
      name: getName(parent, prop),
      type: capitalizeFirstLetter(value.type),
      description: value.description
    });
    return array;
  }));
}
export function flattenResponseSchema(obj, oas, parent = '', level = 0) {
  if (!obj) {
    Raven.captureMessage('Undefined object passed to flattenResponseSchema', {
      extra: {
        object: obj
      }
    });
    return [];
  }
  if (level > 2) {
    return [];
  }

  // Handle top-level schema types (array, oneOf, allOf)
  let newParent;
  if (obj.type === 'array' && obj.items) {
    if (obj.items.$ref) {
      const value = findSchemaDefinition(obj.items.$ref, oas);
      return flattenResponseSchema(value, oas);
    }
    newParent = parent ? `${parent}.[]` : '';
    return flattenResponseSchema(obj.items, oas, `${newParent}`, level + 1);
  }
  if (obj.oneOf) {
    return flattenOneOfSchema(obj, oas, level);
  }
  if (obj.allOf) {
    return getAllOfParams(obj.allOf, parent, level, oas);
  }
  if (obj.additionalProperties && obj.additionalProperties.$ref) {
    return flattenAddtionalProperties(obj, oas);
  }
  if (!obj.properties) {
    return [];
  }
  return flattenObject(obj, parent, level, oas);
}